import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { AlertCircle, CheckCircle2, Calendar, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveApply = () => {
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    leave_type_id: '',
    from_date: '',
    to_date: '',
    is_half_day: false,
    half_day_type: 'first_half',
    reason: ''
  });

  useEffect(() => {
    fetchLeaveTypes();
    fetchLeaveBalance();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const response = await fetch(
        `${API_URL}/api/leave-management/leave-balance/${user?.user_id}?year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setLeaveBalance(data);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const calculateDays = () => {
    if (formData.from_date && formData.to_date) {
      if (formData.is_half_day) return 0.5;
      const from = new Date(formData.from_date);
      const to = new Date(formData.to_date);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const getAvailableBalance = (leaveTypeId) => {
    const balance = leaveBalance.find(b => b.leave_type_id === leaveTypeId);
    if (!balance) return 'Not allocated';
    return `${balance.current_balance - balance.pending} days available`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/apply-leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave application submitted successfully!' });
        setFormData({
          leave_type_id: '',
          from_date: '',
          to_date: '',
          is_half_day: false,
          half_day_type: 'first_half',
          reason: ''
        });
        fetchLeaveBalance();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to submit leave application' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const totalDays = calculateDays();
  const selectedLeaveType = leaveTypes.find(lt => lt.id === formData.leave_type_id);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Apply for Leave</h1>
        <p className="text-gray-600 mt-1">Submit a new leave application</p>
      </div>

      {/* Leave Balance Summary */}
      {leaveBalance.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {leaveBalance.map((balance) => (
            <Card key={balance.id} className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4">
                <div className="text-xs text-gray-600 mb-1">{balance.leave_type_name}</div>
                <div className="text-2xl font-bold text-blue-900">
                  {balance.current_balance - balance.pending}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {balance.pending > 0 && `(${balance.pending} pending)`}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Leave Application Form
          </CardTitle>
        </CardHeader>
        <CardContent>
          {message.text && (
            <div
              className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Leave Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leave Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.leave_type_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, leave_type_id: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{type.name} ({type.short_code})</span>
                        <span className="text-xs text-gray-500 ml-4">
                          {getAvailableBalance(type.id)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedLeaveType && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedLeaveType.description}
                </p>
              )}
            </div>

            {/* Half Day Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_half_day"
                checked={formData.is_half_day}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    is_half_day: e.target.checked,
                    to_date: e.target.checked ? formData.from_date : formData.to_date
                  })
                }
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label htmlFor="is_half_day" className="text-sm font-medium text-gray-700">
                Half Day Leave
              </label>
            </div>

            {/* Half Day Type (if half day is selected) */}
            {formData.is_half_day && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Half Day Type
                </label>
                <Select
                  value={formData.half_day_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, half_day_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_half">First Half</SelectItem>
                    <SelectItem value="second_half">Second Half</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.from_date}
                  onChange={(e) => {
                    const newFormData = { ...formData, from_date: e.target.value };
                    if (formData.is_half_day) {
                      newFormData.to_date = e.target.value;
                    }
                    setFormData(newFormData);
                  }}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.to_date}
                  onChange={(e) =>
                    setFormData({ ...formData, to_date: e.target.value })
                  }
                  required
                  min={formData.from_date || new Date().toISOString().split('T')[0]}
                  disabled={formData.is_half_day}
                />
              </div>
            </div>

            {/* Total Days Display */}
            {totalDays > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>Total Days:</strong> {totalDays} day{totalDays > 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please provide a reason for your leave application..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setFormData({
                    leave_type_id: '',
                    from_date: '',
                    to_date: '',
                    is_half_day: false,
                    half_day_type: 'first_half',
                    reason: ''
                  })
                }
              >
                Reset
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                <Send className="h-4 w-4" />
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveApply;